
import { getRunnitNodeRunResultFilePath, getRunnitUploadFilePath, RunnitNodeRunResult, RunnitUpload, RUNNIT_NODE_RUN_RESULT_TYPE } from '@run-diffusion/shared';
import Vue from 'vue';
import { mapActions, mapState } from 'vuex';
import { storage } from '@/firebase';
import { RunnitsImageSelectMixin } from '@/mixins/RunnitsImageSelectMixin';
import { NODE_RUN_RESULT_SELECTION_MODE } from './constants';
import _findIndex from 'lodash/findIndex';
import _get from 'lodash/get';
import {
	GALLERY_SELECTION_CONTEXT_KEY,
	isGalleryMultiSelection,
	isGallerySelectionMode,
	isGallerySingleSelection,
	isSelectedInGalleryContext,
} from '@/components/ImageGallery/gallerySelectionContext';

export default Vue.extend({
	name: 'RunnitVideo',
	inject: {
		gallerySelectionContext: { from: GALLERY_SELECTION_CONTEXT_KEY, default: null },
	},
	props: {
		nodeRunResult: { type: Object, default: null },
		upload: { type: Object, default: null },
		isThumb: { type: Boolean, default: false },
		noClick: { type: Boolean, default: false },
		height: { type: String, default: null },
		maxHeight: { type: String, default: null },
		width: { type: String, default: null },
		draggable: { type: Boolean, default: false },
		isDragging: { type: Boolean, default: false },
	},
	mixins: [
		RunnitsImageSelectMixin,
	],
	data () {
		return {
			showGif: false,
			thumbSrc: null,
			thumbError: false,
			gifSrc: null,
			vidSrc: null,
			mp4Src: null,
			srcIsFetching: false,
			srcIsError: false,
			srcIsLoaded: false,

			// Lazy loading
			isVisible: false,
			isInViewport: false,
			observer: null,

			// Video controls
			isVideoPaused: false,

			// Drag functionality
			dragTimeout: null,
		};
	},
	computed: {
		...mapState([
			'runnitState',
			'user',
			'dragAndDrop',
		]),
		isSelectionMode () {
			if (this.gallerySelectionContext) {
				return isGallerySelectionMode(this.gallerySelectionContext, RUNNIT_NODE_RUN_RESULT_TYPE.VID, this.noClick);
			}
			return !!(!this.noClick && this.currentSelectionState.nodeRunResultSelectionMode && this.currentSelectionState.canSelectTypes.includes(RUNNIT_NODE_RUN_RESULT_TYPE.VID));
		},
		isSingleSelection () {
			if (this.gallerySelectionContext) {
				return isGallerySingleSelection(this.gallerySelectionContext);
			}
			return this.currentSelectionState.nodeRunResultSelectionMode === NODE_RUN_RESULT_SELECTION_MODE.SINGLE;
		},
		isMultiSelection () {
			if (this.gallerySelectionContext) {
				return isGalleryMultiSelection(this.gallerySelectionContext);
			}
			return this.currentSelectionState.nodeRunResultSelectionMode === NODE_RUN_RESULT_SELECTION_MODE.MULTIPLE;
		},
		isSelected () {
			if (this.gallerySelectionContext) {
				return isSelectedInGalleryContext(this.gallerySelectionContext, {
					nodeRunResultUuid: this.nodeRunResult?.uuid || null,
					uploadId: this.upload?.id || null,
				});
			}
			return !!(
				this.currentSelectionState.selectedNodeRunResults.length &&
				this.nodeRunResult &&
				_findIndex(this.currentSelectionState.selectedNodeRunResults, (n: RunnitNodeRunResult) => (n.uuid === this.nodeRunResult.uuid)) !== -1
			);
		},
		canBeDragged () {
			// Check if there's an active drop zone
			const hasActiveDropZone = this.dragAndDrop && this.dragAndDrop.activeDropZones > 0;

			// Check if we're already dragging something
			const isAlreadyDragging: boolean = !!(this.dragAndDrop && this.dragAndDrop.isDragging && this.dragAndDrop.draggedItem);

			// Don't allow starting a new drag if we're already dragging something
			if (isAlreadyDragging) {
				return false;
			}

			// Only allow dragging if there's a drop zone and we have a valid video
			return !!(this.draggable && hasActiveDropZone && (this.vidSrc || this.mp4Src));
		},
		autoPlayThumbnailsOff () {
			return this._get(this.user, 'runnitSettings.autoPlayThumbnailsOff');
		},
	},
	watch: {
		nodeRunResult: {
			immediate: true,
			async handler (newVal: RunnitNodeRunResult, oldVal: RunnitNodeRunResult) {
				const newFileName: string = this._get(newVal, 'file.name') || null;
				const oldFileName: string = this._get(oldVal, 'file.name') || null;
				if (newVal && newFileName !== oldFileName && this.isVisible) {
					await this.fetchSrc(newVal);
				}
			},
		},
		upload: {
			immediate: true,
			async handler (newVal: RunnitUpload, oldVal: RunnitUpload) {
				const newFileName: string = this._get(newVal, 'name') || null;
				const oldFileName: string = this._get(oldVal, 'name') || null;
				if (newVal && newFileName !== oldFileName && this.isVisible) {
					await this.fetchSrc(newVal);
				}
			},
		},
		vidSrc: {
			immediate: true,
			handler (newVal: string, oldVal: string) {
				if (newVal && newVal !== oldVal) {
					if (this.$refs.videoPlayer) {
						// Only mute for thumbnails/previews, not for full videos in carousel
						if (this.isThumb) {
							this.$refs.videoPlayer.muted = true;
						}
						this.$refs.videoPlayer.load();
					}
					// Handle playback state after video loads
					this.$nextTick(() => {
						this.handleVideoPlayback();
					});
				}
			}
		},
		mp4Src: {
			immediate: true,
			handler (newVal: string, oldVal: string) {
				if (newVal && newVal !== oldVal) {
					if (this.$refs.videoPlayer) {
						// Only mute for thumbnails/previews, not for full videos in carousel
						if (this.isThumb) {
							this.$refs.videoPlayer.muted = true;
						}
						this.$refs.videoPlayer.load();
					}
					// Handle playback state after video loads
					this.$nextTick(() => {
						this.handleVideoPlayback();
					});
				}
			}
		},
		'user.runnitSettings.autoPlayThumbnailsOff': {
			handler (newVal: boolean, oldVal: boolean) {
				if (newVal !== oldVal) {
					// When autoplay setting changes, update video playback immediately
					this.$nextTick(() => {
						this.handleVideoPlayback();
					});
				}
			}
		}
	},
	mounted () {
		// Setup intersection observer for lazy loading and play/pause control
		this.observer = new IntersectionObserver(
			(entries) => {
				entries.forEach(entry => {
					if (entry.isIntersecting) {
						if (!this.isVisible) {
							this.isVisible = true;
							// Start loading the video
							if (this.nodeRunResult) {
								this.fetchSrc(this.nodeRunResult);
							} else if (this.upload) {
								this.fetchSrc(this.upload);
							}
						}
						this.isInViewport = true;
						this.handleVideoPlayback();
					} else {
						this.isInViewport = false;
						this.handleVideoPlayback();
					}
				});
			},
			{
				rootMargin: '500px 0px', // Start loading when within 500px of viewport
				threshold: 0.01 // Trigger when 1% of the element is visible
			}
		);

		// Start observing the container
		if (this.$el) {
			this.observer.observe(this.$el);
		}
	},
	beforeDestroy () {
		if (this.observer) {
			this.observer.disconnect();
		}
		// Clear drag timeout if it exists
		if (this.dragTimeout) {
			clearTimeout(this.dragTimeout);
			this.dragTimeout = null;
		}
	},
	methods: {
		...mapActions([
			'updateRunnitState',
			'cacheRunnitVideoUrl',
			'setIsDragging',
			'setDraggedItem',
		]),
		async retryLoopFetchSrc (filePath: string, numTries: number, srcType: string) {
			// Check cache first
			if (this.runnitState.videoCache[filePath]) {
				this[srcType] = this.runnitState.videoCache[filePath];
				return;
			}

			const fileRef = storage.ref(filePath);
			let count: number = 1;
			while (true) {
				try {
					const url: string = await fileRef.getDownloadURL();
					// Cache the URL
					this.cacheRunnitVideoUrl({ filePath, url });
					this[srcType] = url;
					break;
				} catch (e) {
					if (count >= numTries) throw e;

					count++;
					await new Promise(resolve => setTimeout(resolve, 250)); // wait before next retry
				}
			}
		},
		async fetchSrc (input: RunnitNodeRunResult | RunnitUpload) {
			// Prevent concurrent calls
			if (this.srcIsFetching) {
				return;
			}

			const nodeRunResult: RunnitNodeRunResult = input as RunnitNodeRunResult;
			const upload: RunnitUpload = input as RunnitUpload;

			let filePath, thumbFilePath, gifFilePath, mp4FilePath;
			if (this.nodeRunResult) {
				const filePathData = getRunnitNodeRunResultFilePath(nodeRunResult);
				filePath = filePathData.filePath;
				thumbFilePath = filePathData.thumbFilePath;
				gifFilePath = filePathData.gifFilePath;
				mp4FilePath = filePathData.mp4FilePath;
			} else if (this.upload) {
				const filePathData = getRunnitUploadFilePath(upload);
				filePath = filePathData.filePath;
				thumbFilePath = filePathData.thumbFilePath;
				// For uploads, we don't have gif/mp4 variants, so use the main file
				gifFilePath = filePath;
				mp4FilePath = filePath;
			}

			if (
				(
					this.nodeRunResult &&
					this.nodeRunResult.file &&
					this.nodeRunResult.file.name
				) ||
				(
					this.upload &&
					this.upload.name
				)
			) {
				try {
					this.srcIsFetching = true;
					this.srcIsError = false;
					this.srcIsLoaded = false;

					if (this.isThumb) {
						// Try to load mp4 first (condensed video for better performance in thumbnails)
						try {
							await this.retryLoopFetchSrc(mp4FilePath, 1, 'mp4Src');
							return; // If mp4 loads successfully, we're done
						} catch (e) {
							// Mp4 doesn't exist yet, continue with fallback strategy
							console.log('Mp4 not available, falling back to thumb/gif strategy');
						}

						try {
							await Promise.all([
								this.retryLoopFetchSrc(thumbFilePath, 1, 'thumbSrc'),
								this.retryLoopFetchSrc(gifFilePath, 1, 'gifSrc')
							]);
						} catch (e) {
							// The thumb img wasn't created yet, so just default to the original
							await this.retryLoopFetchSrc(filePath, 10, 'vidSrc');
						}
					} else {
						await this.retryLoopFetchSrc(filePath, 10, 'vidSrc');
					}
				} catch (e) {
					console.error(e);
					this.srcIsError = true;
				} finally {
					this.srcIsFetching = false;
				}
			}
		},
		onClick () {
			if (this.noClick) return;

			if (this.isSelectionMode) {
				// TODO the value emitted from this isn't actually used.
				if (this.isSingleSelection) {
					this.$emit('on-single-select', {
						nodeRunResult: this.nodeRunResult,
						upload: this.upload,
						avatar: this.avatar,
					});
				} else if (this.isMultiSelection) {
					this.$emit('on-multi-select', {
						nodeRunResult: this.nodeRunResult,
						upload: this.upload,
						avatar: this.avatar,
					});
				}
			} else {
				this.$emit('on-click');
			}
		},
		handleThumbError () {
			this.thumbError = true;
		},
		handleVideoPlayback () {
			const videoPlayer = this.$refs.videoPlayer;
			if (!videoPlayer) return;

			// Thumbnail/previews can autoplay in galleries; full-size videos should stay under user control.
			if (this.isThumb) {
				videoPlayer.muted = true;
				if (this.isInViewport && !this._get(this.user, 'runnitSettings.autoPlayThumbnailsOff')) {
					videoPlayer.loop = true; // Ensure loop is set programmatically
					videoPlayer.play().catch(e => {
						// Handle play() promise rejection (e.g., user hasn't interacted with page yet)
						console.error('Video play was prevented:', e);
					});
				} else {
					videoPlayer.pause();
				}
				return;
			}

			if (!this.isInViewport) {
				videoPlayer.pause();
			}
		},
		togglePlayPause () {
			const videoPlayer = this.$refs.videoPlayer;
			if (!videoPlayer) return;

			// Only mute for thumbnails/previews, not for full videos in carousel
			if (this.isThumb) {
				videoPlayer.muted = true;
			}

			if (videoPlayer.paused) {
				videoPlayer.play();
				this.isVideoPaused = false;
			} else {
				videoPlayer.pause();
				this.isVideoPaused = true;
			}
		},
		handleDragStart (ev) {
			if (!this.canBeDragged) {
				return;
			}

			// Create a custom drag image instead of using the full video
			const dragPreview = document.createElement('div');
			dragPreview.style.width = '100px';
			dragPreview.style.height = '100px';
			dragPreview.style.borderRadius = '6px';
			dragPreview.style.overflow = 'hidden';
			dragPreview.style.position = 'absolute';
			dragPreview.style.top = '-1000px'; // Position off-screen
			dragPreview.style.background = 'var(--grey-900)';
			dragPreview.style.border = '2px solid var(--primary-blue)';

			// Try to create a video preview, but fallback to an icon if video isn't ready
			const videoSrc = this.vidSrc || this.mp4Src;
			if (videoSrc) {
				// Try to use the existing video element if available
				const existingVideo = this.$refs.videoPlayer;
				if (existingVideo && existingVideo.videoWidth > 0) {
					// Use canvas to capture current frame
					this.createVideoFramePreview(dragPreview, existingVideo);
				} else {
					// Create a new video element for preview
					const previewVideo = document.createElement('video');
					previewVideo.src = videoSrc;
					previewVideo.style.width = '100%';
					previewVideo.style.height = '100%';
					previewVideo.style.objectFit = 'cover';
					previewVideo.muted = true;
					previewVideo.loop = true;
					previewVideo.autoplay = true;
					previewVideo.style.borderRadius = '4px';

					// Add error handling for video
					previewVideo.onerror = () => {
						// Fallback to icon if video fails to load
						this.createIconPreview(dragPreview);
					};

					dragPreview.appendChild(previewVideo);
				}
			} else {
				// No video source available, use icon
				this.createIconPreview(dragPreview);
			}

			document.body.appendChild(dragPreview);

			// Use the custom element as drag image
			ev.dataTransfer.setDragImage(dragPreview, 50, 50);

			// Clean up the element after drag starts
			setTimeout(() => {
				if (document.body.contains(dragPreview)) {
					document.body.removeChild(dragPreview);
				}
			}, 0);

			// Set up the drag data
			const dragData: any = {
				type: 'RUNNIT_VIDEO',
				src: this.vidSrc || this.mp4Src
			};

			// If this is a node run result, include that data
			if (this.nodeRunResult) {
				dragData.nodeRunResult = this.nodeRunResult;
			}

			// If this is an upload, include that data
			if (this.upload) {
				dragData.upload = this.upload;
			}

			// Store the drag data in the Vuex store
			this.setDraggedItem(dragData);
			this.setIsDragging(true);

			// Set a timeout to reset the drag state if the drop event doesn't fire
			this.dragTimeout = setTimeout(() => {
				this.setIsDragging(false);
				this.setDraggedItem(null);
				document.body.style.cursor = ''; // Reset cursor
			}, 15000); // 15 seconds timeout
		},
		handleDragEnd (event) {
			// Reset cursor
			document.body.style.cursor = '';

			// Clear the timeout
			if (this.dragTimeout) {
				clearTimeout(this.dragTimeout);
				this.dragTimeout = null;
			}

			// Reset the drag state
			this.setIsDragging(false);
			this.setDraggedItem(null);
		},
		createIconPreview (container) {
			// Clear any existing content
			container.innerHTML = '';

			// Create a video icon preview
			const iconContainer = document.createElement('div');
			iconContainer.style.width = '100%';
			iconContainer.style.height = '100%';
			iconContainer.style.display = 'flex';
			iconContainer.style.alignItems = 'center';
			iconContainer.style.justifyContent = 'center';
			iconContainer.style.background = 'var(--grey-800)';
			iconContainer.style.borderRadius = '4px';

			// Create a video icon (using a simple SVG or text)
			const icon = document.createElement('div');
			icon.innerHTML = '🎥';
			icon.style.fontSize = '32px';
			icon.style.color = 'var(--primary-blue)';
			icon.style.textAlign = 'center';

			iconContainer.appendChild(icon);
			container.appendChild(iconContainer);
		},
		createVideoFramePreview (container, videoElement) {
			try {
				// Create a canvas to capture the current video frame
				const canvas = document.createElement('canvas');
				canvas.width = 100;
				canvas.height = 100;
				canvas.style.borderRadius = '4px';

				const ctx = canvas.getContext('2d');
				if (ctx) {
					// Draw the current video frame to canvas
					ctx.drawImage(videoElement, 0, 0, 100, 100);

					// Add a border to make it look like a drag preview
					ctx.strokeStyle = 'var(--primary-blue)';
					ctx.lineWidth = 2;
					ctx.strokeRect(0, 0, 100, 100);

					container.appendChild(canvas);
				} else {
					// Fallback to icon if canvas fails
					this.createIconPreview(container);
				}
			} catch (error) {
				// Fallback to icon if anything fails
				this.createIconPreview(container);
			}
		},
	},
	components: {
	},
});
